// ─── Lottie library ───────────────────────────────────────────────────────
// Catálogo curado de animaciones Lottie libres usadas por el buscador.
// Todas las URLs apuntan al CDN público de LottieFiles
// (`assets*.lottiefiles.com`) que sirve los `.json` directamente.
//
// Para reemplazar este catálogo por una API real (LottieFiles requiere
// API key), basta con cambiar la implementación de `searchLotties()`.
// La forma del resultado es estable.

export type LottieItem = {
  /** Id único — usamos el slug del archivo. */
  id: string;
  /** Título visible en el tile (tooltip). */
  title: string;
  /** URL pública al .json de Lottie. */
  url: string;
  /** Tags para filtrar por búsqueda. */
  tags: string[];
  /**
   * Duración natural de la animación, en segundos. Se completa al hacer
   * pick (leyendo `op/fr` del JSON) — no se conoce de antemano. Si está
   * presente, el editor usa este valor como duración del clip insertado.
   */
  durationSec?: number;
};

// Selección curada. **Solo URLs verificadas que devuelven 200** sobre el
// CDN de LottieFiles. El catálogo de URLs `lf20_*` históricamente es
// inestable: muchos slugs se purgan o cambian de visibilidad. Si querés
// agregar más, primero verificá con un `curl -sI` o usá el input "Pegar
// URL" en la UI (más permisivo: el usuario asume el riesgo).
export const LOTTIE_LIBRARY: LottieItem[] = [
  {
    id: 'check-success',
    title: 'Check de éxito',
    url: 'https://assets10.lottiefiles.com/packages/lf20_lk80fpsm.json',
    tags: ['check', 'éxito', 'verde', 'tilde', 'ok'],
  },
  {
    id: 'loading-dots',
    title: 'Puntos cargando',
    url: 'https://assets3.lottiefiles.com/packages/lf20_usmfx6bp.json',
    tags: ['loading', 'cargando', 'puntos', 'spinner'],
  },
  {
    id: 'celebration',
    title: 'Celebración',
    url: 'https://assets2.lottiefiles.com/packages/lf20_obhph3sh.json',
    tags: ['celebración', 'fiesta', 'confeti', 'party'],
  },
  {
    id: 'thumbs-up',
    title: 'Pulgar arriba',
    url: 'https://assets1.lottiefiles.com/packages/lf20_jbrw3hcz.json',
    tags: ['like', 'pulgar', 'thumbs', 'aprobación'],
  },
  {
    id: 'message-bubble',
    title: 'Burbuja de chat',
    url: 'https://assets3.lottiefiles.com/packages/lf20_jcikwtux.json',
    tags: ['chat', 'mensaje', 'burbuja', 'conversación', 'message'],
  },
  {
    id: 'lock',
    title: 'Candado seguro',
    url: 'https://assets10.lottiefiles.com/packages/lf20_yr6zz3wv.json',
    tags: ['candado', 'lock', 'seguridad', 'secure', 'privacy'],
  },
  {
    id: 'cloud-upload',
    title: 'Subir a la nube',
    url: 'https://assets3.lottiefiles.com/packages/lf20_ndt8zfny.json',
    tags: ['nube', 'cloud', 'subir', 'upload', 'archivo'],
  },
];

/**
 * Valida una URL pegada por el usuario y, si no es usable, devuelve una
 * sugerencia clara. Ser permisivo importa: muchas URLs válidas no
 * terminan en `.json` (ej. `cloudfront.net/abc?token=…`).
 *
 * Casos detectados:
 *   - URL no es http(s) → inválida.
 *   - URL es la página pública de LottieFiles (`/free-animation/…`,
 *     `/animations/…`) → inválida con explicación específica.
 *   - Cualquier otra URL → válida (la carga del recurso decidirá).
 */
export type LottieUrlValidation =
  | { valid: true }
  | { valid: false; hint: string };

export const validateLottieUrl = (raw: string): LottieUrlValidation => {
  const trimmed = raw.trim();
  if (!trimmed) return { valid: false, hint: '' };
  if (!/^https?:\/\//i.test(trimmed)) {
    return { valid: false, hint: 'Tiene que ser una URL http o https.' };
  }
  if (/^https?:\/\/(www\.)?lottiefiles\.com\/(free-animation|animations)\//i.test(trimmed)) {
    return {
      valid: false,
      hint:
        'Esa es la URL de la página, no del archivo. Abrí la animación, ' +
        'apretá el botón "Download" → "Lottie JSON" y copiá ese enlace ' +
        '(termina en .json o .lottie).',
    };
  }
  return { valid: true };
};

/**
 * Busca animaciones por título o tag. Insensible a mayúsculas/acentos.
 * Para enchufar la API real de LottieFiles (requiere API key), cambiar
 * el cuerpo por un fetch a su endpoint y mapear el resultado a `LottieItem`.
 */
export const searchLotties = async (query: string): Promise<LottieItem[]> => {
  const q = query.trim().toLowerCase();
  if (!q) return LOTTIE_LIBRARY;
  // Normalización básica: sacamos acentos para que "celebracion" matchee
  // con "celebración".
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const nq = norm(q);
  return LOTTIE_LIBRARY.filter(item => {
    if (norm(item.title).includes(nq)) return true;
    return item.tags.some(t => norm(t).includes(nq));
  });
};
